import { Component, OnInit } from "@angular/core";
import { NgxSpinnerService } from "ngx-spinner";
import { HttpHelpService } from "../../service/http-help.service";
import { TranslateService } from "@ngx-translate/core";
import { ActivatedRoute } from "@angular/router";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import Swal from "sweetalert2";
import { forkJoin } from "rxjs";
import {
  InvoiceSubscriptionsV2Service,
  InvoiceSubscriptionStatus,
  InvoiceSubscriptionPlan,
} from "src/app/services/invoice-subscriptions-v2.service";

@Component({
  selector: "app-view-merchant",
  templateUrl: "./view-merchant.component.html",
  styleUrls: ["./view-merchant.component.scss"]
})

export class ViewMerchantComponent implements OnInit {
  curentLang:string="";
  layoutVersion:any;
  userRegistId:any = JSON.parse(localStorage.getItem('user_id'));
  merchantId = JSON.parse(localStorage.getItem('merchant_id'));
  user_type = JSON.parse(localStorage.getItem('user_type'));
  merchantInfoData:any=[];
  id:number;
  MerchantNameEn:string;
  MerchantNameAr:string;
  MerchantNameHe:string;
  MerchantAddress:string;
  IdNumber:string;
  public validate = false;

  /** Unified shape for template (admin or merchant invoice-subscriptions-v2 APIs) */
  v2SubscriptionStatus: any = null;
  v2SubscriptionLoading = false;
  v2SubscriptionError = false;

  /** Admins always; merchants/assistants only when viewing their own merchant row */
  get showSubscriptionSection(): boolean {
    const ut = Number(this.user_type);
    if (ut === 200 || ut === 201) return true;
    const routeId = Number(this.id);
    const mid = this.merchantId != null ? Number(this.merchantId) : NaN;
    if ([100, 101, 300, 301].includes(ut) && !Number.isNaN(routeId) && routeId === mid) return true;
    return false;
  }

  updateMerchant=new FormGroup({
    merchant_name_en:new FormControl('' , Validators.required),
    merchant_name_ar :new FormControl('' , Validators.required),  
    merchant_name_he:new FormControl('', Validators.required),
    merchant_address:new FormControl('', Validators.required),
    id_number:new FormControl('', Validators.required),
    updated_by:new FormControl(''),
  });

  newOrders = {
    colorClass: "primary",
    icon: "new-order",
    colorClass2: "warning",
    icon2: "customers",
    colorClass3: "success",
    icon4: "invoice",
    colorClass4: "secondary",
    icon3: "profit"
  };
  constructor
  (  
     private spinner: NgxSpinnerService ,
    public translate:TranslateService ,
    private service:HttpHelpService  ,
    private invoiceSubscriptionsV2: InvoiceSubscriptionsV2Service,
    activatedRoute:ActivatedRoute,
    ) 
  { 
    this.curentLang = localStorage.getItem('curentLang');
    this.id = activatedRoute.snapshot.params['id'];
    this.user_type = JSON.parse(localStorage.getItem('user_type'));
  };

  getMerchantInfoById() {
    this.spinner.show();
    this.service.getMerchantInfoById(this.id).subscribe({
      next: res => {
        this.merchantInfoData = res;       
        this.MerchantNameEn = this.merchantInfoData[0]?.merchant_name_en
        this.MerchantNameAr = this.merchantInfoData[0]?.merchant_name_ar
        this.MerchantNameHe = this.merchantInfoData[0]?.merchant_name_he
        this.MerchantAddress = this.merchantInfoData[0]?.merchant_address
        this.IdNumber = this.merchantInfoData[0]?.id_number
        this.spinner.hide();
        this.loadInvoiceSubscriptionV2();
      },
      error: error => {
        this.spinner.hide();
      }
    });
  };

  loadInvoiceSubscriptionV2(): void {
    if (!this.showSubscriptionSection) {
      this.v2SubscriptionStatus = null;
      return;
    }
    this.v2SubscriptionLoading = true;
    this.v2SubscriptionError = false;

    const isAdmin = this.user_type === 200 || this.user_type === 201;
    if (isAdmin) {
      this.service.getAdminMerchantSubscriptionStatus(this.id).subscribe({
        next: (res) => {
          this.v2SubscriptionStatus = this.normalizeAdminSubscriptionResponse(res);
          this.v2SubscriptionLoading = false;
        },
        error: () => {
          this.v2SubscriptionLoading = false;
          this.v2SubscriptionError = true;
          this.v2SubscriptionStatus = null;
        },
      });
      return;
    }

    forkJoin({
      status: this.invoiceSubscriptionsV2.getStatus(),
      plans: this.invoiceSubscriptionsV2.getPlans(),
    }).subscribe({
      next: ({ status, plans }) => {
        const planId = status?.subscription?.plan_id;
        const plan = planId ? (plans || []).find((p: InvoiceSubscriptionPlan) => p.id === planId) : undefined;
        this.v2SubscriptionStatus = this.mergeMerchantStatusWithPlan(status, plan);
        this.v2SubscriptionLoading = false;
      },
      error: () => {
        this.v2SubscriptionLoading = false;
        this.v2SubscriptionError = true;
        this.v2SubscriptionStatus = null;
      },
    });
  }

  private normalizeAdminSubscriptionResponse(res: any): any {
    if (!res?.subscription) return res || null;
    const sub = res.subscription;
    if (sub.usage) return res;
    if (
      sub.documents_used !== undefined ||
      sub.documents_limit !== undefined ||
      sub.companies_used !== undefined ||
      sub.companies_limit !== undefined
    ) {
      return {
        ...res,
        subscription: {
          ...sub,
          usage: {
            documents_used: sub.documents_used ?? 0,
            documents_limit: sub.documents_limit ?? 0,
            companies_used: sub.companies_used ?? 0,
            companies_limit: sub.companies_limit ?? 0,
          },
        },
      };
    }
    return res;
  }

  private mergeMerchantStatusWithPlan(
    status: InvoiceSubscriptionStatus | null,
    plan: InvoiceSubscriptionPlan | undefined
  ): any {
    if (!status) return null;
    const s = status.subscription;
    if (!s) {
      return {
        days_remaining: status.days_remaining,
        documents_remaining: status.documents_remaining,
        companies_remaining: status.companies_remaining,
        is_active: status.is_active,
        is_expired: status.is_expired,
        subscription: null,
      };
    }
    const planShim = plan
      ? { id: plan.id, name_ar: plan.name_ar, name_en: plan.name_en, name_he: plan.name_he }
      : { id: s.plan_id };
    return {
      days_remaining: status.days_remaining,
      documents_remaining: status.documents_remaining,
      companies_remaining: status.companies_remaining,
      is_active: status.is_active,
      is_expired: status.is_expired,
      subscription: {
        ...s,
        plan: planShim,
        usage: {
          documents_used: s.documents_used ?? 0,
          documents_limit: s.documents_limit ?? 0,
          companies_used: s.companies_used ?? 0,
          companies_limit: s.companies_limit ?? 0,
        },
      },
    };
  }

  getPlanDisplayName(plan: any): string {
    if (!plan) return "—";
    if (this.curentLang === "ar") return plan.name_ar || plan.name_en || plan.name_he || `#${plan.id}`;
    if (this.curentLang === "he") return plan.name_he || plan.name_en || plan.name_ar || `#${plan.id}`;
    return plan.name_en || plan.name_ar || plan.name_he || `#${plan.id}`;
  }

  getSubscriptionUsage(): { documents_used: number; documents_limit: number; companies_used: number; companies_limit: number } | null {
    const sub = this.v2SubscriptionStatus?.subscription;
    if (!sub) return null;
    const u = sub.usage || this.v2SubscriptionStatus?.usage;
    if (u) {
      return {
        documents_used: u.documents_used ?? 0,
        documents_limit: u.documents_limit ?? 0,
        companies_used: u.companies_used ?? 0,
        companies_limit: u.companies_limit ?? 0,
      };
    }
    if (
      sub.documents_used !== undefined ||
      sub.documents_limit !== undefined ||
      sub.companies_used !== undefined ||
      sub.companies_limit !== undefined
    ) {
      return {
        documents_used: sub.documents_used ?? 0,
        documents_limit: sub.documents_limit ?? 0,
        companies_used: sub.companies_used ?? 0,
        companies_limit: sub.companies_limit ?? 0,
      };
    }
    return null;
  }

  copyPendingPaymentLink(url?: string | null): void {
    const link = url || this.v2SubscriptionStatus?.latest_pending_admin_payment_link;
    if (!link || typeof link !== "string") return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(link).then(() => {
        Swal.fire({
          icon: "success",
          title: this.curentLang === "ar" ? "تم نسخ رابط الدفع" :
            (this.curentLang === "en" ? "Payment link copied" : "קישור התשלום הועתק")
        });
      }).catch(() => this.copyPaymentLinkFallback(link));
      return;
    }
    this.copyPaymentLinkFallback(link);
  }

  private copyPaymentLinkFallback(link: string): void {
    const textarea = document.createElement("textarea");
    textarea.value = link;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
    Swal.fire({
      icon: "success",
      title: this.curentLang === "ar" ? "تم نسخ رابط الدفع" :
        (this.curentLang === "en" ? "Payment link copied" : "קישור התשלום הועתק")
    });
  }


  ngOnInit() {
    this.getMerchantInfoById();

  this.layoutVersion = localStorage.getItem("layout_version");
   if (this.layoutVersion == null || this.layoutVersion == undefined) {
     localStorage.setItem("layout_version", "light-only");
     this.layoutVersion= "light-only"; 
   } else {
     this.layoutVersion = localStorage.getItem("layout_version"); 
     if (this.layoutVersion === "dark-only") {
       document.body.classList.add("dark-only");
     } else {
       document.body.classList.add("light-only");

     }
   }
   this.userRegistId = JSON.parse(localStorage.getItem('user_id'));
   this.merchantId = JSON.parse(localStorage.getItem('merchant_id'));

  };
}
